import React, { useState, useEffect, useRef, useCallback } from "react";
import { database, functions } from "../firebaseConfig";
import { ref, get, onValue, update } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import "./LeadSpySettings.css";
import "./SeoAnalyzerSettings.css";

const COUNTRIES = [
  { code: 'de', label: '🇩🇪 Germany' },
  { code: 'at', label: '🇦🇹 Austria' },
  { code: 'ch', label: '🇨🇭 Switzerland' },
  { code: 'us', label: '🇺🇸 United States' },
  { code: 'uk', label: '🇬🇧 United Kingdom' },
  { code: 'es', label: '🇪🇸 Spain' },
  { code: 'fr', label: '🇫🇷 France' },
  { code: 'it', label: '🇮🇹 Italy' },
  { code: 'nl', label: '🇳🇱 Netherlands' },
];

const DEFAULT_LIMITS = {
  competitors: 3,
  opportunities: 5,
};

const DEBOUNCE_MS = 1200;

// Module credit cost is the Sistrix API credit cost per scan.
// Dynamic modules (competitors, opportunities) compute cost from the user's limit.
const STATIC_MODULE_COSTS = {
  coreMetrics: 3,
  rankingDistribution: 1,
  backlinkCount: 1,
  trafficValue: 1,
  visibilityTrend: 12,
};

const buildModules = (limits) => ({
  coreMetrics: {
    id: 'coreMetrics',
    name: 'Core Metrics',
    description: 'Visibility score, total keyword count, and top-10 keyword count — the three essentials',
    icon: 'fa-chart-line',
    credits: STATIC_MODULE_COSTS.coreMetrics,
    isRequired: true,
    features: ['Visibility Index', 'Keywords Total', 'Keywords in Top 10'],
    targetFields: ['Visibility Score', 'Keyword Count', 'Top-10 Count'],
    whatYouGet: {
      summary: 'A quick health snapshot of how strong this domain is on Google.',
      points: [
        'A visibility score that sums up their overall Google ranking strength',
        'How many keywords the site ranks for in total',
        'How many of those already sit in the Top 10',
      ],
    },
  },
  rankingDistribution: {
    id: 'rankingDistribution',
    name: 'Ranking Distribution',
    description: 'How many keywords rank on each SERP page (1-10) — reveals SEO potential',
    icon: 'fa-list-ol',
    credits: STATIC_MODULE_COSTS.rankingDistribution,
    isRequired: false,
    features: ['Page 1-10 breakdown', 'SERP position heatmap'],
    targetFields: ['Ranking Distribution'],
    whatYouGet: {
      summary: 'Shows where their rankings actually sit across Google result pages.',
      points: [
        'How many keywords rank on each results page (1 to 10)',
        'Reveals whether they are stuck on page 2–3 (big upside) or already own page 1',
      ],
    },
  },
  backlinkCount: {
    id: 'backlinkCount',
    name: 'Backlink Count',
    description: 'Total backlinks, referring hosts, domains, and IP networks',
    icon: 'fa-link',
    credits: STATIC_MODULE_COSTS.backlinkCount,
    isRequired: false,
    features: ['Total Backlinks', 'Referring Hosts', 'Referring Domains', 'Network Diversity'],
    targetFields: ['Backlink Count', 'Referring Domains'],
    whatYouGet: {
      summary: 'The size and quality of the domain’s backlink profile.',
      points: [
        'Total number of backlinks pointing at the site',
        'How many different websites link to them',
        'How diverse those links are (a quality vs. spam signal)',
      ],
    },
  },
  trafficValue: {
    id: 'trafficValue',
    name: 'Traffic & Value',
    description: 'Estimated monthly organic visitors and their € value — the most tangible outreach hook',
    icon: 'fa-euro-sign',
    credits: STATIC_MODULE_COSTS.trafficValue,
    isRequired: false,
    features: ['Est. Monthly Visitors', 'Est. Traffic Value (€)'],
    targetFields: ['Est. Traffic', 'Traffic Value'],
    whatYouGet: {
      summary: 'Turns SEO into money — perfect for a sales pitch.',
      points: [
        'Estimated organic visitors the site gets from Google each month',
        'What that traffic is worth in euros (based on keyword ad prices)',
        'Only costs 1 Sistrix credit — extrapolated from the top page',
      ],
    },
  },
  visibilityTrend: {
    id: 'visibilityTrend',
    name: 'Visibility Trend',
    description: 'Is the brand gaining or losing Google visibility? Full 12-month curve — the strongest sales hook',
    icon: 'fa-arrow-trend-down',
    credits: STATIC_MODULE_COSTS.visibilityTrend,
    isRequired: false,
    isPremium: true,
    features: ['12-Month Curve', 'Up/Down Direction', '% Change'],
    targetFields: ['Visibility Trend'],
    whatYouGet: {
      summary: 'How their Google visibility developed over the last full year.',
      points: [
        'A monthly visibility value for each of the last 12 months vs. today',
        'A clear up / down direction and the % change over the year',
        'Brands that are losing visibility are your hottest leads',
        'Costs 12 Sistrix credits (one dated lookup per month)',
      ],
    },
  },
  competitors: {
    id: 'competitors',
    name: 'Competitor Intel',
    description: 'Top comparable organic competitors — marketplaces & portals (Amazon, idealo…) filtered out',
    icon: 'fa-users',
    credits: limits.competitors,
    dynamicCredits: true,
    isRequired: false,
    features: ['Comparable Competitors', 'Shared Keywords Match Score'],
    targetFields: ['Competitors'],
    whatYouGet: {
      summary: 'Who really competes with this domain for the same Google keywords.',
      points: [
        'A ranked list of genuinely comparable competitors',
        'Generic giants (Amazon, eBay, idealo, price portals…) are automatically removed',
        'A match score showing how much keyword overlap you share',
        'You choose how many competitors to pull (1 credit each, +a few to filter)',
      ],
    },
  },
  opportunities: {
    id: 'opportunities',
    name: 'Opportunities',
    description: 'Low-hanging fruit keywords ranking 11-20 with highest gain potential — outreach gold',
    icon: 'fa-bullseye',
    credits: limits.opportunities,
    dynamicCredits: true,
    isRequired: false,
    isPremium: true,
    features: ['High-Gain Keywords', 'Current Position', 'Target URL', 'Competition Score'],
    targetFields: ['Opportunities'],
    whatYouGet: {
      summary: 'Their fastest SEO wins — perfect hooks for outreach.',
      points: [
        'Keywords that rank just outside the top spots (quick wins)',
        'Current position and the exact page that ranks',
        'Estimated traffic gain if pushed up, and how contested it is',
        'You choose how many keywords to pull (1 credit each)',
      ],
    },
  },
});

const SeoAnalyzerSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [activeModules, setActiveModules] = useState(['coreMetrics']);
  const [infoOpen, setInfoOpen] = useState(null);
  const [country, setCountry] = useState('de');
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, sistrixCreditsUsed: 0 });

  // ── API key state ──
  const [keyStatus, setKeyStatus] = useState({ hasKey: false, apiKeyPreview: null, loading: true });
  const [keyInput, setKeyInput] = useState('');
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyError, setKeyError] = useState(null);
  const [editingKey, setEditingKey] = useState(false);

  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const reconcileTriedRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    const settingsRef = ref(database, `/World/${userId}/settings/seoAnalyzerSettings`);
    get(settingsRef).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        if (Array.isArray(data.activeModules)) {
          const validIds = ['coreMetrics', 'rankingDistribution', 'backlinkCount', 'trafficValue', 'visibilityTrend', 'competitors', 'opportunities'];
          const filtered = data.activeModules.filter((id) => validIds.includes(id));
          setActiveModules(filtered.length ? filtered : ['coreMetrics']);
        }
        if (data.country) setCountry(data.country);
        if (data.limits) setLimits((prev) => ({ ...prev, ...data.limits }));
      }
      setLoading(false);
      requestAnimationFrame(() => { hasLoadedRef.current = true; });
    });

    const statsRef = ref(database, `/World/${userId}/stats/seoAnalyzer`);
    const unsubStats = onValue(statsRef, (snapshot) => {
      if (snapshot.exists()) setStats(snapshot.val());
    });

    return () => {
      unsubStats();
    };
  }, [userId]);

  // The presence of a Sistrix key is tracked by a persistent, client-readable
  // flag at World/{userId}/settings/seoAnalyzerSettings/hasApiKey. This is the
  // single source of truth the whole app reads (research gating, bulk, spy) —
  // no backend call is needed to know whether a key exists. The encrypted key
  // itself stays backend-only in /SecretIntegrations.
  useEffect(() => {
    if (!userId) return;
    reconcileTriedRef.current = false;
    const settingsRef = ref(database, `/World/${userId}/settings/seoAnalyzerSettings`);

    const unsub = onValue(settingsRef, (snapshot) => {
      const v = snapshot.val() || {};

      if (typeof v.hasApiKey === 'boolean') {
        setKeyStatus({ hasKey: v.hasApiKey, apiKeyPreview: v.apiKeyPreview || null, loading: false });
        return;
      }

      // Flag not initialised yet (e.g. key stored before the flag existed).
      // Self-heal exactly once: ask the backend, then persist the flag so every
      // future read is flag-only (no more backend calls).
      if (reconcileTriedRef.current) {
        setKeyStatus({ hasKey: false, apiKeyPreview: null, loading: false });
        return;
      }
      reconcileTriedRef.current = true;
      const statusFn = httpsCallable(functions, 'getSeoAnalyzerApiKeyStatus');
      statusFn()
        .then((res) => {
          const hasKey = res.data?.hasKey === true;
          const preview = res.data?.apiKeyPreview || null;
          update(ref(database, `/World/${userId}/settings/seoAnalyzerSettings`), {
            hasApiKey: hasKey,
            apiKeyPreview: preview,
          }).catch(() => {});
          setKeyStatus({ hasKey, apiKeyPreview: preview, loading: false });
        })
        .catch(() => setKeyStatus({ hasKey: false, apiKeyPreview: null, loading: false }));
    });

    return () => unsub();
  }, [userId]);

  const saveApiKey = useCallback(async () => {
    const key = keyInput.trim();
    if (!key || keyBusy) return;
    setKeyBusy(true);
    setKeyError(null);
    try {
      const fn = httpsCallable(functions, 'setSeoAnalyzerApiKey');
      const res = await fn({ apiKey: key });
      const preview = res.data?.apiKeyPreview || null;
      // Persist the client-readable flag at the world path everyone reads from,
      // so research gating (bulk/spy) reacts immediately — no backend re-check.
      await update(ref(database, `/World/${userId}/settings/seoAnalyzerSettings`), {
        hasApiKey: true,
        apiKeyPreview: preview,
        apiKeyUpdatedAt: Date.now(),
      }).catch(() => {});
      setKeyStatus({ hasKey: true, apiKeyPreview: preview, loading: false });
      setKeyInput('');
      setEditingKey(false);
    } catch (error) {
      setKeyError(error?.message || 'Could not save the API key.');
    } finally {
      setKeyBusy(false);
    }
  }, [keyInput, keyBusy, userId]);

  const removeApiKey = useCallback(async () => {
    if (keyBusy) return;
    setKeyBusy(true);
    setKeyError(null);
    try {
      const fn = httpsCallable(functions, 'deleteSeoAnalyzerApiKey');
      await fn({});
      await update(ref(database, `/World/${userId}/settings/seoAnalyzerSettings`), {
        hasApiKey: false,
        apiKeyPreview: null,
        apiKeyUpdatedAt: Date.now(),
      }).catch(() => {});
      setKeyStatus({ hasKey: false, apiKeyPreview: null, loading: false });
      setKeyInput('');
      setEditingKey(false);
    } catch (error) {
      setKeyError(error?.message || 'Could not remove the API key.');
    } finally {
      setKeyBusy(false);
    }
  }, [keyBusy, userId]);

  const persistSettings = useCallback(async (modules, countryCode, limitValues) => {
    if (!userId) return;
    setSaveStatus('saving');
    try {
      const settingsRef = ref(database, `/World/${userId}/settings/seoAnalyzerSettings`);
      await update(settingsRef, {
        activeModules: modules,
        country: countryCode,
        limits: limitValues,
        updatedAt: Date.now(),
      });
      setSaveStatus('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
    } catch (error) {
      console.error("Error saving SEO Analyzer settings:", error);
      setSaveStatus('idle');
    }
  }, [userId]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    clearTimeout(debounceRef.current);
    setSaveStatus('idle');
    debounceRef.current = setTimeout(() => {
      persistSettings(activeModules, country, limits);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [activeModules, country, limits, persistSettings]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedTimerRef.current);
    };
  }, []);

  const modules = buildModules(limits);

  const calculateCredits = (active = activeModules) => {
    return active.reduce((total, id) => total + (modules[id]?.credits || 0), 0);
  };

  const toggleModule = (moduleId) => {
    const mod = modules[moduleId];
    if (mod?.isRequired) return;
    setActiveModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const updateLimit = (key, value) => {
    setLimits((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="spySettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  const totalCredits = calculateCredits();
  const budgetWarning = totalCredits > 5;

  return (
    <div className="spySettingsContainer">
      <div className="spySettingsHeader">
        <div className="spySettingsTitle">
          <div className="spySettingsIcon">
            <i className="fa fa-magnifying-glass-chart" style={{ fontSize: 22, color: 'var(--darkblue)' }} />
          </div>
          <div className="spySettingsTitleText">
            <h3>SEO Analyzer</h3>
            <p>Per-lead visibility, keyword rankings, competitors & backlinks</p>
          </div>
        </div>
        <div className="spyCreditsDisplay">
          <div className="creditsBalance">
            <span className="creditsLabel">Default Budget</span>
            <span className="creditsValue">3 credits</span>
          </div>
          <div className="creditsCost" style={{ background: budgetWarning ? '#fff4e5' : undefined }}>
            <span className="costLabel">
              {budgetWarning ? '⚠ Over budget' : 'Per Scan'}
            </span>
            <span className="costValue">{totalCredits} credits</span>
          </div>
        </div>
      </div>

      <div className="seoKeyCard">
        <div className="seoKeyCardHead">
          <div className="seoKeyCardIcon">
            <i className="fa fa-key" />
          </div>
          <div className="seoKeyCardTitle">
            <h4>Sistrix API Key</h4>
            <p>Your own Sistrix API key. Stored encrypted in the backend — the SEO Analyzer only activates once a key is saved.</p>
          </div>
          <div className={`seoKeyStatusPill ${keyStatus.hasKey ? 'connected' : 'missing'}`}>
            <span className="seoKeyStatusDot" />
            {keyStatus.loading ? 'Checking…' : keyStatus.hasKey ? 'Connected' : 'Not set'}
          </div>
        </div>

        {!keyStatus.loading && keyStatus.hasKey && !editingKey ? (
          <div className="seoKeyConnectedRow">
            <div className="seoKeyPreview">
              <i className="fa fa-lock" />
              <code>{keyStatus.apiKeyPreview || '••••'}</code>
            </div>
            <div className="seoKeyActions">
              <button className="seoKeyBtn seoKeyBtnGhost" onClick={() => { setEditingKey(true); setKeyError(null); }} disabled={keyBusy}>
                <i className="fa fa-pen" /> Replace
              </button>
              <button className="seoKeyBtn seoKeyBtnDanger" onClick={removeApiKey} disabled={keyBusy}>
                <i className="fa fa-trash" /> Remove
              </button>
            </div>
          </div>
        ) : (
          !keyStatus.loading && (
            <div className="seoKeyInputRow">
              <div className="seoKeyInputWrap">
                <i className="fa fa-key" />
                <input
                  type="password"
                  className="seoKeyInput"
                  placeholder="Paste your Sistrix API key…"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveApiKey(); }}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <button className="seoKeyBtn seoKeyBtnPrimary" onClick={saveApiKey} disabled={keyBusy || !keyInput.trim()}>
                {keyBusy ? <><i className="fa fa-spinner fa-spin" /> Verifying…</> : <><i className="fa fa-check" /> Save & Verify</>}
              </button>
              {keyStatus.hasKey && editingKey && (
                <button className="seoKeyBtn seoKeyBtnGhost" onClick={() => { setEditingKey(false); setKeyInput(''); setKeyError(null); }} disabled={keyBusy}>
                  Cancel
                </button>
              )}
            </div>
          )
        )}

        {keyError && (
          <div className="seoKeyError">
            <i className="fa fa-exclamation-circle" />
            <span>{keyError}</span>
          </div>
        )}
      </div>

      <div className="spyModulesSection">
        <div className="modulesSectionHeader">
          <h4>
            <i className="fa fa-puzzle-piece"></i>
            Analysis Modules
          </h4>
          <div className="modulesSummary">
            <span className="modulesActive">{activeModules.length} active</span>
            <span className="modulesDivider">•</span>
            <span className="modulesCredits">{totalCredits} Sistrix credits/scan</span>
          </div>
        </div>

        <div className="modulesGrid">
          {Object.entries(modules).map(([moduleId, mod]) => (
            <div
              key={moduleId}
              className={`moduleCard ${activeModules.includes(moduleId) ? 'active' : ''} ${mod.isRequired ? 'required' : ''} ${mod.isPremium ? 'premium' : ''}`}
              onClick={() => toggleModule(moduleId)}
            >
              <div className="moduleCardHeader">
                <div className="moduleIcon">
                  <i className={`fa ${mod.icon}`}></i>
                </div>
                <div className="moduleToggle">
                  {mod.isRequired ? (
                    <span className="requiredBadge">Required</span>
                  ) : (
                    <div className={`toggleSwitch ${activeModules.includes(moduleId) ? 'on' : ''}`}>
                      <div className="toggleKnob"></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="moduleCardBody">
                <h5 className="moduleName">
                  {mod.name}
                  {mod.isPremium && <i className="fa fa-crown premiumIcon"></i>}
                </h5>
                <p className="moduleDesc">{mod.description}</p>

                {mod.whatYouGet && (
                  <div className="seoInfoWrap">
                    <button
                      type="button"
                      className={`seoInfoToggle ${infoOpen === moduleId ? 'open' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setInfoOpen((prev) => (prev === moduleId ? null : moduleId));
                      }}
                    >
                      <i className="fa fa-circle-info" />
                      <span>What you get</span>
                      <i className={`fa fa-chevron-${infoOpen === moduleId ? 'up' : 'down'} seoInfoChevron`} />
                    </button>
                    {infoOpen === moduleId && (
                      <div className="seoInfoPanel" onClick={(e) => e.stopPropagation()}>
                        <p className="seoInfoSummary">{mod.whatYouGet.summary}</p>
                        <ul className="seoInfoList">
                          {mod.whatYouGet.points.map((point, idx) => (
                            <li key={idx}>
                              <i className="fa fa-check" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="moduleCardFooter">
                <div className="moduleFeatures">
                  {mod.features.slice(0, 3).map((feature, idx) => (
                    <span key={idx} className="moduleFeature">
                      <i className="fa fa-check"></i>
                      {feature}
                    </span>
                  ))}
                  {mod.features.length > 3 && (
                    <span className="moduleFeatureMore">+{mod.features.length - 3} more</span>
                  )}
                </div>
                <div className="moduleCredits">
                  <span className="creditValue">{mod.credits}</span>
                  <span className="creditLabel">{mod.dynamicCredits ? 'credits*' : 'credits'}</span>
                </div>
              </div>

              <div className="moduleTargetFields">
                <i className="fa fa-database"></i>
                <span className="targetFieldsLabel">Saves to:</span>
                <span className="targetFieldsList">
                  {mod.targetFields.map((field, idx) => (
                    <code key={idx} className="targetField">{field}</code>
                  ))}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="advancedSettingsSection">
        <div className="advancedHeader">
          <h4>
            <i className="fa fa-cog"></i>
            SEO Analyzer Configuration
          </h4>
        </div>

        <div className="advancedGrid">
          <div className="advancedCard fullWidth">
            <div className="advancedCardHeader">
              <div className="advancedCardIcon">
                <i className="fa fa-globe"></i>
              </div>
              <div className="advancedCardTitle">
                <h5>Target Country Index</h5>
                <p>Which Google country index to pull rankings and visibility from</p>
              </div>
            </div>
            <div className="advancedCardBody">
              <div className="languageOptions">
                {COUNTRIES.map((c) => (
                  <button
                    key={c.code}
                    className={`langOption ${country === c.code ? 'active' : ''}`}
                    onClick={() => setCountry(c.code)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {activeModules.includes('competitors') && (
            <div className="advancedCard">
              <div className="advancedCardHeader">
                <div className="advancedCardIcon">
                  <i className="fa fa-users"></i>
                </div>
                <div className="advancedCardTitle">
                  <h5>Competitors Per Scan</h5>
                  <p>1 Sistrix credit per competitor returned</p>
                </div>
              </div>
              <div className="advancedCardBody">
                <label>Competitor Limit ({limits.competitors} credits)</label>
                <div className="thresholdSlider">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={limits.competitors}
                    onChange={(e) => updateLimit('competitors', parseInt(e.target.value))}
                  />
                  <span className="thresholdValue">{limits.competitors}</span>
                </div>
              </div>
            </div>
          )}

          {activeModules.includes('opportunities') && (
            <div className="advancedCard">
              <div className="advancedCardHeader">
                <div className="advancedCardIcon">
                  <i className="fa fa-bullseye"></i>
                </div>
                <div className="advancedCardTitle">
                  <h5>Opportunities Per Scan</h5>
                  <p>1 Sistrix credit per opportunity keyword</p>
                </div>
              </div>
              <div className="advancedCardBody">
                <label>Opportunity Limit ({limits.opportunities} credits)</label>
                <div className="thresholdSlider">
                  <input
                    type="range"
                    min="1"
                    max="15"
                    step="1"
                    value={limits.opportunities}
                    onChange={(e) => updateLimit('opportunities', parseInt(e.target.value))}
                  />
                  <span className="thresholdValue">{limits.opportunities}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {budgetWarning && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            background: '#fff4e5',
            border: '1px solid #ffb454',
            borderRadius: 8,
            fontSize: 13,
            color: '#8a5a00',
          }}>
            <i className="fa fa-triangle-exclamation" style={{ marginRight: 8 }} />
            You're currently configured for <strong>{totalCredits} Sistrix credits per scan</strong>, above the recommended 3-5 credit budget. Consider reducing optional modules or their limits.
          </div>
        )}
      </div>

      <div className="creditCalculatorSection">
        <div className="calculatorCard">
          <div className="calculatorHeader">
            <h4>
              <i className="fa fa-calculator"></i>
              Sistrix Credit Forecast
            </h4>
          </div>
          <div className="calculatorBody">
            <div className="calculatorRow">
              <span className="calcLabel">Credits / Scan</span>
              <span className="calcValue">{totalCredits}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">100 Scans</span>
              <span className="calcValue">{(totalCredits * 100).toLocaleString()}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">1,000 Scans</span>
              <span className="calcValue">{(totalCredits * 1000).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="calculatorCard statsCard">
          <div className="calculatorHeader">
            <h4>
              <i className="fa fa-chart-bar"></i>
              Usage Statistics
            </h4>
          </div>
          <div className="calculatorBody">
            <div className="calculatorRow">
              <span className="calcLabel">Total Scans</span>
              <span className="calcValue">{(stats.total || 0).toLocaleString()}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">This Month</span>
              <span className="calcValue">{(stats.thisMonth || 0).toLocaleString()}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">Sistrix Credits Used</span>
              <span className="calcValue">{(stats.sistrixCreditsUsed || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`spyAutoSaveIndicator ${saveStatus}`}>
        {saveStatus === 'saving' && (
          <>
            <span className="autoSaveSpinner"></span>
            <span>Saving…</span>
          </>
        )}
        {saveStatus === 'saved' && (
          <>
            <i className="fa fa-check"></i>
            <span>Saved</span>
          </>
        )}
      </div>
    </div>
  );
};

export default SeoAnalyzerSettings;
